/**
 * SubstitutionStatusChip — Phase 5 read-only governance badge.
 *
 * Aggregates a job's substitution governance into a single chip:
 *   🔴 Substitution Pending Review
 *   🔴 Substitution Incompatible
 *   🟡 Conditional Compatibility
 *   🟢 Approved Compatible Substitute
 *
 * Pure display over `useSubstitutionRequests`. No writers, no mutations.
 */
import { Badge } from "@/components/ui/badge";
import { Replace, AlertTriangle, XCircle, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSubstitutionRequests } from "@/data/procurement/useSubstitutionRequests";

interface Props {
  jobId: string;
  className?: string;
  hideWhenSilent?: boolean;
}

export function SubstitutionStatusChip({ jobId, className, hideWhenSilent = true }: Props) {
  const s = useSubstitutionRequests(jobId);
  const total = s.all.length;

  if (total === 0) {
    if (hideWhenSilent) return null;
    return null;
  }

  const blocker =
    s.pending.length > 0 || s.incompatibleOpen.length > 0;
  const warn = s.approvedConditional.length > 0;
  const onlyApprovedCompatible =
    !blocker && !warn && s.approvedCompatible.length > 0;

  if (!blocker && !warn && !onlyApprovedCompatible && hideWhenSilent) return null;

  let label: string;
  let icon: JSX.Element;
  let cls: string;
  let testid: string;

  if (s.pending.length > 0) {
    label = `Substitution Pending Review${s.pending.length > 1 ? ` (${s.pending.length})` : ""}`;
    icon = <XCircle className="h-3 w-3" />;
    cls = "bg-destructive/10 text-destructive ring-1 ring-destructive/30 border-destructive/30";
    testid = "substitution-chip-pending";
  } else if (s.incompatibleOpen.length > 0) {
    label = `Substitution Incompatible (${s.incompatibleOpen.length})`;
    icon = <XCircle className="h-3 w-3" />;
    cls = "bg-destructive/10 text-destructive ring-1 ring-destructive/30 border-destructive/30";
    testid = "substitution-chip-incompatible";
  } else if (warn) {
    label = `Conditional Compatibility (${s.approvedConditional.length})`;
    icon = <AlertTriangle className="h-3 w-3" />;
    cls = "bg-warning/10 text-warning ring-1 ring-warning/30 border-warning/30";
    testid = "substitution-chip-conditional";
  } else {
    label = `Approved Substitute${s.approvedCompatible.length > 1 ? ` (${s.approvedCompatible.length})` : ""}`;
    icon = <CheckCircle2 className="h-3 w-3" />;
    cls = "bg-success/10 text-success ring-1 ring-success/30 border-success/30";
    testid = "substitution-chip-approved";
  }

  return (
    <Badge
      variant="outline"
      className={cn("gap-1 text-[10px] font-medium", cls, className)}
      data-testid={testid}
      title={label}
    >
      <Replace className="h-3 w-3" />
      {icon}
      <span className="truncate max-w-[200px]">{label}</span>
    </Badge>
  );
}
